import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import {
    AzureSqlDWLinkedService,
    ReferenceAdapter,
    Credential,
    AdapterContext,
    CredentialTypeOptions,
    azureCloudTypeDef,
    sqlAdditionalPropertiesAdapts,
    sqlAdditionalPropertiesKeyList,
    ParameterConfig,
    SqlAdditionalPropertiesKeyType,
    SQLAuthenticationType,
    azureSqlDWAuthRefTypes
} from 'shell-models-lib';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { Constants } from 'app/shared/shared-constants';
import {
    akvToResource,
    akvToModel,
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {
    connectionStringPartToResource,
    connectionStringPartToModel
} from 'app/shared/utils/linkedservice-parameter.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { GlobalVariables } from 'app/services/globals';
import { toParameterClass, toParameterObject } from './parameter-config.util';
import { compact } from 'lodash';
import { sqlAdditionalPropertiesKeyOptions } from 'app/registries/consts/metadata-consts';
import { sqlConnectionStringKeyAlias } from './azure-sql-linkedservice.adapter';

const sqlDWV1Adapts = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    connectionString: (_context: AdapterContext, _value: any, ls: AzureSqlDWLinkedService) => {
        // when AKV is used for connection string, it is already well handled. So we don't need handle it.
        // won't cover fully by json for now
        if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
            if (ls.authenticationType === SQLAuthenticationType.SQL && ls.password instanceof AzureKeyVault) {
                return '*';
            }
            if (
                ls.authenticationType === SQLAuthenticationType.ManagedServiceIdentity ||
                ls.authenticationType === SQLAuthenticationType.UserAssignedManagedIdentity ||
                ls.authenticationType === SQLAuthenticationType.Credential
            ) {
                return '*';
            }
            if (
                ls.authenticationType === SQLAuthenticationType.ServicePrincipal &&
                (ls.servicePrincipalKey instanceof AzureKeyVault ||
                    (ls.credential != null && ls.credential.getType() === CredentialTypeOptions.ServicePrincipal))
            ) {
                return '*';
            }
        }
        return 'secret';
    },
    servicePrincipalKey: 'secret'
};

const sqlDWV2Adapts = {
    server: '*',
    database: '*',
    userName: '*',
    encrypt: '*',
    trustServerCertificate: '*',
    hostNameInCertificate: '*',
    authenticationType: '*',
    servicePrincipalCredential: 'secret',
    ...sqlAdditionalPropertiesAdapts
};

const sharedAdapts = {
    password: 'secret',
    encryptedCredential: '*',
    servicePrincipalId: '*',
    tenant: '*',
    azureCloudType: '*',
    credential: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureSqlDWLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                ...sharedAdapts,
                ...sqlDWV1Adapts,
                ...sqlDWV2Adapts
            },
            parameters: '*'
        }
    }
})
export class AzureSqlDWLinkedServiceAdapter extends LinkedServiceAdapter<AzureSqlDWLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureSqlDWLinkedService): Promise<void> {
        if (model.sqlVersion === 'v2') {
            return;
        }

        let finalAuthType: SQLAuthenticationType = model.authenticationType;
        if (model.credential) {
            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = SQLAuthenticationType.ServicePrincipal;
            } else if (credType === CredentialTypeOptions.ManagedIdentity) {
                finalAuthType = SQLAuthenticationType.ManagedServiceIdentity;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = SQLAuthenticationType.UserAssignedManagedIdentity;
            } else {
                finalAuthType = model.authenticationType;
            }
        }
        model.authenticationType = finalAuthType;
    }

    protected modelConstructor(): new () => AzureSqlDWLinkedService {
        return AzureSqlDWLinkedService;
    }

    protected model(): IModel {
        return AzureSqlDWLinkedService.MODEL;
    }

    protected override _parseConnectionString(
        connectionString: string,
        linkedService: AzureSqlDWLinkedService,
        additionalFieldsParameterizable: boolean = false
    ): void {
        try {
            this._updateModelFromConnectionString(
                connectionString,
                linkedService,
                false,
                additionalFieldsParameterizable
            );
            const hasUserId =
                !stringIsNullOrEmpty(AzureSqlDWLinkedService.USER_NAME.templateValue(linkedService)) ||
                !stringIsNullOrEmpty(AzureSqlDWLinkedService.USER_NAME.value(linkedService));
            if (hasUserId) {
                linkedService.authenticationType = SQLAuthenticationType.SQL;
            }
        } catch (e) {
            this._logError(
                `AzureSqlLinkedServiceAdapter parse connection string error: "connection string": ${connectionString}, "error":${e}`
            );
        }
    }

    protected async propertiesToResourceAsync(
        linkedService: AzureSqlDWLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureSqlDWLinkedService;
        properties.type = Metamodel.modelFor(AzureSqlDWLinkedService).name;

        let servicePrincipalId: string;
        let servicePrincipalKey: string | Model.SecretBase;
        let tenant: string;
        let azureCloudType: azureCloudTypeDef;
        const isSP = linkedService.authenticationType === SQLAuthenticationType.ServicePrincipal;
        const isInlineSP =
            isSP && linkedService.authReferenceMethod === azureSqlDWAuthRefTypes.InlineRefTypeAuthentication;
        if (isInlineSP) {
            servicePrincipalId = this.valueToResource(linkedService, AzureSqlDWLinkedService.SERVICE_PRINCIPAL_ID);
            servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                AzureSqlDWLinkedService.SERVICE_PRINCIPAL_KEY,
                this.context
            );
            tenant = this.valueToResource(linkedService, AzureSqlDWLinkedService.TENANT);
            azureCloudType = linkedService.azureCloudType;
        }

        const credential =
            linkedService.credential &&
            ((isSP && !isInlineSP) ||
                linkedService.authenticationType === SQLAuthenticationType.UserAssignedManagedIdentity ||
                (GlobalVariables.isSamiSupportEnabled &&
                    linkedService.authenticationType === SQLAuthenticationType.ManagedServiceIdentity &&
                    linkedService.authReferenceMethod === azureSqlDWAuthRefTypes.CredentialRefTypeAuthentication))
                ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                : undefined;

        if (linkedService.sqlVersion === 'v2') {
            properties.typeProperties = {
                server: this.valueToResource(linkedService, AzureSqlDWLinkedService.SERVER_NAME),
                database: this.valueToResource(linkedService, AzureSqlDWLinkedService.DATABASE_NAME),
                encrypt: this.valueToResource(linkedService, AzureSqlDWLinkedService.ENCRYPT),
                trustServerCertificate: this.valueToResource(
                    linkedService,
                    AzureSqlDWLinkedService.TRUST_SERVER_CERTIFICATE
                ),
                hostNameInCertificate:
                    (AzureSqlDWLinkedService.HOST_NAME_IN_CERTIFICATE.templateValue(linkedService) ||
                        linkedService.hostNameInCertificate) &&
                    (!stringIsNullOrEmpty(
                        AzureSqlDWLinkedService.TRUST_SERVER_CERTIFICATE.templateValue(linkedService)
                    ) ||
                        linkedService.trustServerCertificate !== true)
                        ? this.valueToResource(linkedService, AzureSqlDWLinkedService.HOST_NAME_IN_CERTIFICATE)
                        : undefined,
                authenticationType: linkedService.authenticationType,
                servicePrincipalId,
                servicePrincipalCredential: isInlineSP ? servicePrincipalKey : undefined,
                tenant,
                azureCloudType,
                userName:
                    linkedService.authenticationType === SQLAuthenticationType.SQL
                        ? this.valueToResource(linkedService, AzureSqlDWLinkedService.USER_NAME)
                        : undefined,
                password:
                    linkedService.authenticationType === SQLAuthenticationType.SQL
                        ? linkedService.password instanceof AzureKeyVault
                            ? akvToResource(linkedService.password, this.context)
                            : parameterizablePasswordOrAKVtoSecretBase(
                                  linkedService,
                                  AzureSqlDWLinkedService.PASSWORD,
                                  this.context
                              )
                        : undefined,
                credential
            };

            if (linkedService.parameterizableAdditionalFields?.length > 0) {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
                };
            }
        } else {
            let connectionString: string | Model.SecretBase;

            if (!linkedService.enableAKV) {
                const builder = new DbConnectionStringBuilderService();
                builder.add('Integrated Security', AzureSqlDWLinkedService.integratedSecurity);
                builder.add('Encrypt', AzureSqlDWLinkedService.encrypt);
                builder.add('Connection Timeout', AzureSqlDWLinkedService.connectionTimeout);

                builder.add(
                    'Data Source',
                    connectionStringPartToResource(linkedService, AzureSqlDWLinkedService.SERVER_NAME)
                );
                const databaseString = connectionStringPartToResource(
                    linkedService,
                    AzureSqlDWLinkedService.DATABASE_NAME
                );
                builder.add('Initial Catalog', databaseString);

                if (linkedService.authenticationType === SQLAuthenticationType.SQL) {
                    builder.add(
                        'User ID',
                        connectionStringPartToResource(linkedService, AzureSqlDWLinkedService.USER_NAME)
                    );
                    if (!(linkedService.password instanceof AzureKeyVault)) {
                        const password = connectionStringPartToResource(
                            linkedService,
                            AzureSqlDWLinkedService.PASSWORD
                        );
                        if (password) {
                            builder.add('Password', password);
                        }
                    }
                }
                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        builder.add(pair.key, pair.value || '');
                    });
                }

                connectionString =
                    linkedService.serverName === Constants.maskedCredential &&
                    linkedService.databaseName === Constants.maskedCredential &&
                    linkedService.userName === Constants.maskedCredential &&
                    linkedService.password === Constants.maskedCredential
                        ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          ({
                              type: 'SecureString',
                              value:
                                  linkedService.serverName === Constants.maskedCredential
                                      ? Constants.maskedCredential
                                      : builder.connectionString
                          } as Model.SecureString)
                        : this._connectionStringToResource(builder.connectionString);
            } else {
                connectionString = akvToResource(linkedService.referedAKV, this.context);
            }

            properties.typeProperties = {
                connectionString,
                password:
                    !linkedService.enableAKV &&
                    linkedService.authenticationType === SQLAuthenticationType.SQL &&
                    linkedService.password instanceof AzureKeyVault
                        ? akvToResource(linkedService.password, this.context)
                        : undefined,
                servicePrincipalId,
                servicePrincipalKey,
                azureCloudType,
                tenant,
                credential
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: AzureSqlDWLinkedService
    ): Promise<AzureSqlDWLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureSqlDWLinkedService;

        if (!__IS_FABRIC_DPE__ && properties.typeProperties.connectionString == null) {
            linkedService.sqlVersion = 'v2';
            ValueAdapter.toModel(linkedService, AzureSqlDWLinkedService.SERVER_NAME, properties.typeProperties.server);
            ValueAdapter.toModel(
                linkedService,
                AzureSqlDWLinkedService.DATABASE_NAME,
                properties.typeProperties.database
            );
            ValueAdapter.toModel(linkedService, AzureSqlDWLinkedService.ENCRYPT, properties.typeProperties.encrypt);
            ValueAdapter.toModel(
                linkedService,
                AzureSqlDWLinkedService.TRUST_SERVER_CERTIFICATE,
                properties.typeProperties.trustServerCertificate
            );
            ValueAdapter.toModel(
                linkedService,
                AzureSqlDWLinkedService.HOST_NAME_IN_CERTIFICATE,
                properties.typeProperties.hostNameInCertificate
            );
            linkedService.authenticationType = properties.typeProperties.authenticationType as SQLAuthenticationType;

            if (linkedService.authenticationType === SQLAuthenticationType.SQL) {
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDWLinkedService.USER_NAME,
                    properties.typeProperties.userName
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureSqlDWLinkedService.PASSWORD,
                    properties.typeProperties.password,
                    this.context
                );
            } else if (
                linkedService.authenticationType === SQLAuthenticationType.ServicePrincipal &&
                properties.typeProperties.servicePrincipalId
            ) {
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDWLinkedService.SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.servicePrincipalId
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureSqlDWLinkedService.SERVICE_PRINCIPAL_KEY,
                    properties.typeProperties.servicePrincipalCredential,
                    this.context
                );
                ValueAdapter.toModel(linkedService, AzureSqlDWLinkedService.TENANT, properties.typeProperties.tenant);
                linkedService.authReferenceMethod = azureSqlDWAuthRefTypes.InlineRefTypeAuthentication;
                linkedService.azureCloudType = properties.typeProperties.azureCloudType;
            } else if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential,
                    this.context
                );
                linkedService.authReferenceMethod = azureSqlDWAuthRefTypes.CredentialRefTypeAuthentication;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const additionalPropertiesObj: { [key: string]: any } = {};
            for (const propertyName in properties.typeProperties) {
                if (sqlAdditionalPropertiesKeyList.includes(propertyName)) {
                    additionalPropertiesObj[propertyName] =
                        properties.typeProperties[propertyName as keyof Model.AzureSqlDWLinkedServiceTypeProperties];
                }
            }
            linkedService.parameterizableAdditionalFields = toParameterClass(
                additionalPropertiesObj,
                linkedService
            ) as Array<ParameterConfig<SqlAdditionalPropertiesKeyType>>;
        } else {
            linkedService.sqlVersion = 'v1';
            AzureSqlDWLinkedService.SERVER_NAME.setValue(linkedService, undefined);
            AzureSqlDWLinkedService.SERVER_NAME.setTemplateValue(linkedService, undefined);
            AzureSqlDWLinkedService.DATABASE_NAME.setValue(linkedService, undefined);
            AzureSqlDWLinkedService.DATABASE_NAME.setTemplateValue(linkedService, undefined);
            AzureSqlDWLinkedService.USER_NAME.setValue(linkedService, undefined);
            AzureSqlDWLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
            AzureSqlDWLinkedService.PASSWORD.setValue(linkedService, undefined);
            AzureSqlDWLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
            AzureSqlDWLinkedService.SERVICE_PRINCIPAL_ID.setValue(linkedService, undefined);
            AzureSqlDWLinkedService.SERVICE_PRINCIPAL_ID.setTemplateValue(linkedService, undefined);
            AzureSqlDWLinkedService.SERVICE_PRINCIPAL_KEY.setValue(linkedService, undefined);
            AzureSqlDWLinkedService.SERVICE_PRINCIPAL_KEY.setTemplateValue(linkedService, undefined);
            linkedService.authenticationType = SQLAuthenticationType.SQL;
            linkedService.credential = undefined;
            linkedService.referedAKV = undefined;

            let hasUserId = false;
            if (isString(properties.typeProperties.connectionString)) {
                this._updateModelFromConnectionString(properties.typeProperties.connectionString, linkedService);
                hasUserId =
                    !stringIsNullOrEmpty(AzureSqlDWLinkedService.USER_NAME.templateValue(linkedService)) ||
                    !stringIsNullOrEmpty(AzureSqlDWLinkedService.USER_NAME.value(linkedService));
                if (properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        AzureSqlDWLinkedService.PASSWORD,
                        properties.typeProperties.password,
                        this.context
                    );
                }
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
                linkedService.serverName = Constants.maskedCredential;
                linkedService.databaseName = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                linkedService.password = Constants.maskedCredential;
                if (properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        AzureSqlDWLinkedService.PASSWORD,
                        properties.typeProperties.password,
                        this.context
                    );
                }
            } else if (
                properties.typeProperties.connectionString &&
                (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret'
            ) {
                linkedService.referedAKV = akvToModel(
                    properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference,
                    this.context
                );
                linkedService.enableAKV = true;
                linkedService.authenticationType = SQLAuthenticationType.SqlOrManagedServiceIdentity;
            }

            if (properties.typeProperties.servicePrincipalId) {
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDWLinkedService.SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.servicePrincipalId
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureSqlDWLinkedService.SERVICE_PRINCIPAL_KEY,
                    properties.typeProperties.servicePrincipalKey,
                    this.context
                );
                ValueAdapter.toModel(linkedService, AzureSqlDWLinkedService.TENANT, properties.typeProperties.tenant);
                linkedService.authenticationType = SQLAuthenticationType.ServicePrincipal;
                AzureSqlDWLinkedService.PASSWORD.setValue(linkedService, undefined);
                AzureSqlDWLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
                linkedService.azureCloudType = properties.typeProperties.azureCloudType;
                linkedService.authReferenceMethod = azureSqlDWAuthRefTypes.InlineRefTypeAuthentication;
            } else if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential,
                    this.context
                );
                linkedService.authReferenceMethod = azureSqlDWAuthRefTypes.CredentialRefTypeAuthentication;
                AzureSqlDWLinkedService.USER_NAME.setValue(linkedService, undefined);
                AzureSqlDWLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
                AzureSqlDWLinkedService.PASSWORD.setValue(linkedService, undefined);
                AzureSqlDWLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
            } else if (isString(properties.typeProperties.connectionString) && !hasUserId) {
                linkedService.authenticationType = SQLAuthenticationType.ManagedServiceIdentity;
                AzureSqlDWLinkedService.USER_NAME.setValue(linkedService, undefined);
                AzureSqlDWLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
                AzureSqlDWLinkedService.PASSWORD.setValue(linkedService, undefined);
                AzureSqlDWLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
                linkedService.authReferenceMethod = azureSqlDWAuthRefTypes.InlineRefTypeAuthentication;
            }
        }

        return linkedService;
    }

    private _updateModelFromConnectionString(
        connectionString: string,
        linkedService: AzureSqlDWLinkedService,
        fromLSResource: boolean = true,
        additionalFieldsParameterizable: boolean = false
    ): void {
        const builder = new DbConnectionStringBuilderService();
        builder.parse(connectionString);

        connectionStringPartToModel(
            linkedService,
            AzureSqlDWLinkedService.SERVER_NAME,
            builder.getValueUsingAlias('Data Source', sqlConnectionStringKeyAlias, false) || ''
        );
        connectionStringPartToModel(
            linkedService,
            AzureSqlDWLinkedService.DATABASE_NAME,
            builder.getValueUsingAlias('Initial Catalog', sqlConnectionStringKeyAlias, false) || (__IS_FABRIC_DPE__ ? undefined : '')
        );
        connectionStringPartToModel(
            linkedService,
            AzureSqlDWLinkedService.USER_NAME,
            builder.getValueUsingAlias('User ID', sqlConnectionStringKeyAlias, false)
        );

        if (fromLSResource || builder.getValueUsingAlias('Password', sqlConnectionStringKeyAlias, false)) {
            connectionStringPartToModel(
                linkedService,
                AzureSqlDWLinkedService.PASSWORD,
                builder.getValueUsingAlias('Password', sqlConnectionStringKeyAlias, false) || '',
                fromLSResource
            );
        }

        const remainedKeys = [
            ...sqlConnectionStringKeyAlias['Data Source'],
            ...sqlConnectionStringKeyAlias['Initial Catalog'],
            ...sqlConnectionStringKeyAlias['User ID'],
            ...sqlConnectionStringKeyAlias['Password']
        ];

        if (additionalFieldsParameterizable) {
            if (builder.getValueUsingAlias('Encrypt', sqlConnectionStringKeyAlias, false)) {
                connectionStringPartToModel(
                    linkedService,
                    AzureSqlDWLinkedService.ENCRYPT,
                    builder.getValueUsingAlias('Encrypt', sqlConnectionStringKeyAlias, false)
                );
            }

            const tscVaule = builder.getValueUsingAlias('Trust Server Certificate', sqlConnectionStringKeyAlias, false);
            if (tscVaule) {
                const tscBooleanValue =
                    tscVaule.toLowerCase() === 'true' ? true : tscVaule.toLowerCase() === 'false' ? false : tscVaule;
                if (tscBooleanValue === true || tscBooleanValue === false) {
                    AzureSqlDWLinkedService.TRUST_SERVER_CERTIFICATE.setValue(linkedService, tscBooleanValue);
                } else {
                    connectionStringPartToModel(
                        linkedService,
                        AzureSqlDWLinkedService.TRUST_SERVER_CERTIFICATE,
                        builder.getValueUsingAlias('Trust Server Certificate', sqlConnectionStringKeyAlias, false)
                    );
                }
            }

            if (builder.getValueUsingAlias('Host Name In Certificate', sqlConnectionStringKeyAlias, false)) {
                connectionStringPartToModel(
                    linkedService,
                    AzureSqlDWLinkedService.HOST_NAME_IN_CERTIFICATE,
                    builder.getValueUsingAlias('Host Name In Certificate', sqlConnectionStringKeyAlias, false)
                );
            }
            remainedKeys.push(...[
                'Encrypt',
                ...sqlConnectionStringKeyAlias['Trust Server Certificate'] || 'Trust Server Certificate'
            ]);

            linkedService.parameterizableAdditionalFields = compact(
                builder.getRemainedKeyValuePairs(remainedKeys).map((kv) => {
                    const parameterObject = new ParameterConfig<SqlAdditionalPropertiesKeyType>();
                    const additionalParameter = sqlAdditionalPropertiesKeyOptions.find(
                        (option) => option.value.toLowerCase() === kv.key.toLowerCase().replace(/\s*/g, '')
                    );
                    if (additionalParameter) {
                        parameterObject.name = additionalParameter.value as SqlAdditionalPropertiesKeyType;
                        parameterObject.value = kv.value;
                        parameterObject.type = additionalParameter.type;
                        return parameterObject;
                    }
                    return undefined;
                })
            );
        } else {
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(remainedKeys);
            builder.removeDefaultKeyValuePairs(
                linkedService.additionalFields,
                'Integrated Security',
                AzureSqlDWLinkedService.integratedSecurity
            );
            builder.removeDefaultKeyValuePairs(
                linkedService.additionalFields,
                'Encrypt',
                AzureSqlDWLinkedService.encrypt
            );
            builder.removeDefaultKeyValuePairs(
                linkedService.additionalFields,
                'Connection Timeout',
                AzureSqlDWLinkedService.connectionTimeout
            );
        }
    }
}
